import {controller} from '@github/catalyst'
import {generateAppId, registerAppId} from '@github-ui/app-uuid'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {currentState, updateCurrentState} from '@github-ui/history'
import {createBrowserRouter, RouterProvider, type RouterProviderProps} from '@github-ui/react-router'

import type {NavigatorRouteRegistration} from './app-routing-types'
import {createBrowserHistory} from './create-browser-history'
import type {EmbeddedData} from './embedded-data-types'
import {wrapRouterNavigateForTurboAndSoftNavs} from './future/apply-router-navigate-override'
import {type DataRouterAppRegistrationFn, getReactDataRouterApp} from './future/data-router-app-registry'
import {routesWithProviders} from './future/RoutesWithProviders'
import {wrapWithAppShell} from './future/wrap-with-app-shell'
import {getReactNavigatorApp, type NavigatorAppRegistrationFn} from './navigator-app-registry'
import {NavigatorClientEntry} from './NavigatorClientEntry'
import {preloadAllRouteComponents} from './preload-all-route-components'
import {preloadMatchedDataRouterComponents, preloadMatchedRouteComponents} from './preload-matched-route-components'
import {AppProfiler, ProfilerProvider} from './ProfilerContext'
import {getQueryClient} from './query-client'
import {ReactBaseElement} from './ReactBaseElement'

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'react-app': React.DetailedHTMLProps<React.HTMLAttributes<ReactAppElement>, ReactAppElement>
    }
  }
}

// Copied from React Router
const createKey = () => Math.random().toString(36).substr(2, 8)

// What is this silliness? Is it react or a web component?!
// It's a web component we use to bootstrap react apps within the monolith.
@controller('react-app')
export class ReactAppElement extends ReactBaseElement<EmbeddedData> {
  nameAttribute = 'app-name'
  declare uuid: string
  declare routerOrHistory: RouterProviderProps['router'] | ReturnType<typeof createBrowserHistory>
  appRoutes: NavigatorRouteRegistration[] = []

  get enabledFeatures() {
    if (!this.embeddedDataJSON) return []
    const features = this.embeddedDataJSON.appPayload?.enabled_features || {}
    return Object.keys(features).filter(key => features[key as keyof typeof features])
  }

  get routes() {
    return this.appRoutes || (this.routerOrHistory as RouterProviderProps['router'])?.routes || []
  }

  get navigate() {
    if ('push' in this.routerOrHistory) {
      return this.routerOrHistory.push
    }

    if ('navigate' in this.routerOrHistory) {
      return this.routerOrHistory.navigate
    }
  }

  override connectedCallback() {
    super.connectedCallback()
    this.uuid = generateAppId()
    registerAppId(this.uuid)

    window.addEventListener('popstate', this.popStateListener, true)
  }

  popStateListener = (e: PopStateEvent) => {
    // not having a new state means it's a hash navigation
    if (e.state && this.uuid !== currentState().appId) this.routerOrHistory?.dispose()
  }

  override disconnectedCallback() {
    window.removeEventListener('popstate', this.popStateListener, true)
    this.routerOrHistory?.dispose()
    super.disconnectedCallback()
  }
  // The component that wraps this React app will know if the app should be rendered with
  // date router (instead of navigator router) and it does this by setting
  // a `data-data-router-enabled="true"` attribute on this `<react-app>` element.
  // Remember we might have two apps called `some-cool-app`, because it started as a navigator app
  // (i.e. jsonRoute) and now also exists a data router app (i.e. queryRoute). At runtime, we might
  // toggle between which to use based on feature flags.
  get isDataRouterEnabled() {
    return this.getAttribute('data-data-router-enabled') === 'true'
  }

  get includeAppShell() {
    return this.getAttribute('data-app-shell') === 'true'
  }

  async getReactNode(embeddedData: EmbeddedData, onError: (error: Error) => void): Promise<React.ReactElement> {
    if (this.isDataRouterEnabled) {
      const app = await getReactDataRouterApp(this.name)
      return this.#getDataRouterNode(embeddedData, onError, app.registration)
    }

    const app = await getReactNavigatorApp(this.name)
    return this.#getNavigatorNode(embeddedData, onError, app.registration)
  }

  async #getDataRouterNode(
    embeddedData: EmbeddedData,
    onError: (error: Error) => void,
    registration: DataRouterAppRegistrationFn,
  ) {
    if (embeddedData) {
      this.#extractRailsPartials(embeddedData)

      const queryClient = getQueryClient()
      queryClient.removeQueries({queryKey: [this.name]})
    }
    let {routes} = registration({
      // when we hydrated the queryClient directly, we don't want to add initialData again
      embeddedData,
    })

    if (this.includeAppShell) {
      routes = await wrapWithAppShell(routes, embeddedData)
    }

    this.routerOrHistory = wrapRouterNavigateForTurboAndSoftNavs(
      createBrowserRouter(
        routesWithProviders(routes, {
          appPayload: embeddedData.appPayload,
          ssrError: this.ssrError,
          appName: this.name,
          dataRouterEnabled: true,
        }),
      ),
    )

    await preloadMatchedDataRouterComponents(this.routerOrHistory.routes, this.routerOrHistory.state.location.pathname)

    if (isFeatureEnabled('react_preload_all_routes')) {
      // Preload all other lazy route components in the background with low priority
      preloadAllRouteComponents(routes)
    }

    return (
      <ProfilerProvider appName={this.name} isDataRouterEnabled>
        <AppProfiler id={this.name}>
          <RouterProvider router={this.routerOrHistory} />
        </AppProfiler>
      </ProfilerProvider>
    )
  }

  async #getNavigatorNode(
    embeddedData: EmbeddedData,
    onError: (error: Error) => void,
    registration: NavigatorAppRegistrationFn,
  ) {
    const {App, routes} = registration()
    const initialPath = this.getAttribute('initial-path') as string

    if (this.isLazy) {
      const request = await fetch(initialPath, {
        mode: 'no-cors',
        cache: 'no-cache',
        credentials: 'include',
      })
      const {payload} = await request.json()

      embeddedData.payload = payload
    }

    const window = globalThis.window as Window | undefined

    // Initial path is set by ruby. Anchors are not sent to the server.
    // Therefore anchors must be set explicitly by the client.
    const {pathname, search, hash} = new URL(
      `${initialPath}${window?.location.hash ?? ''}`,
      window?.location.href ?? 'https://github.com',
    )

    // set the `key` property to avoid using `default` as the initial location's `key`.
    updateCurrentState({key: createKey()})

    const history = createBrowserHistory({window})
    this.routerOrHistory = history
    this.appRoutes = routes
    const {key, state} = history.location
    const initialLocation = {
      pathname,
      search,
      hash,
      key,
      state,
    }

    // need to await/preload to avoid suspense flash on initial render
    await preloadMatchedRouteComponents(routes, initialLocation)

    if (isFeatureEnabled('react_preload_all_routes')) {
      // Preload all other lazy route components in the background with low priority
      preloadAllRouteComponents(routes)
    }

    return (
      <ProfilerProvider appName={this.name} isDataRouterEnabled={false}>
        <AppProfiler id={this.name}>
          <NavigatorClientEntry
            appName={this.name}
            initialLocation={initialLocation}
            history={history}
            embeddedData={embeddedData}
            routes={routes}
            App={App}
            ssrError={this.ssrError}
            onError={onError}
          />
        </AppProfiler>
      </ProfilerProvider>
    )
  }

  #extractRailsPartials(embeddedData: EmbeddedData) {
    if (!embeddedData.payload) return

    const embeddedRailsPartials = this.querySelectorAll('rails-partial')

    for (const railsPartial of embeddedRailsPartials) {
      const name = railsPartial.getAttribute('data-partial-name')

      if (!name) continue

      const payload = embeddedData.payload as Record<string, unknown>

      payload[name] = railsPartial.innerHTML
    }
  }

  get isLazy() {
    return this.getAttribute('data-lazy') === 'true'
  }
}
